import { Link as GatsbyLink } from "gatsby";
import {
	forwardRef,
	useCallback,
	type HTMLAttributes,
	type PropsWithChildren,
	type RefObject,
} from "react";
import { fixStoryblokPath } from "../../../utils/storyblok/urls";
import { AppDispatchContext, useAppState } from "../../../context/app/context";
import { useDispatchContext } from "../../../hooks/use-dispatch-context";

export type LinkProps = PropsWithChildren<
	HTMLAttributes<HTMLAnchorElement> & {
		href?: string;
		target?: string;
		linktype?: string;
		tabindex?: number;
		anchor?: string;
		linkId?: string;
	}
>;

const Link = forwardRef<HTMLAnchorElement, LinkProps>(
	(
		{
			href,
			target,
			linktype,
			children,
			tabindex = 0,
			anchor,
			linkId,
			...restProps
		},
		ref,
	) => {
		const { drawerIndices } = useAppState();
		const appDispatch = useDispatchContext(AppDispatchContext);
		const { onClick, ...rest } = restProps;
		const anchorFragment = anchor
			? `#${anchor
					.toLocaleLowerCase()
					.replaceAll(" ", "-")
					.replaceAll(/[^\w -]/g, "")
					.trim()}`
			: "";

		const closeNav = useCallback(() => {
			appDispatch({
				type: "setNavOpen",
				navOpen: false,
			});
			if (drawerIndices.some((value) => value !== null)) {
				appDispatch({
					type: "setDrawerIndices",
					drawerIndices: [null, null],
				});
			}
		}, [appDispatch, drawerIndices]);

		if (!href)
			return (
				<span ref={ref} {...rest}>
					{children}
				</span>
			);

		if (linktype === "email") {
			// Email links: add `mailto:` scheme and map to <a>
			return (
				<a href={`mailto:${href}`} ref={ref} {...rest}>
					{children}
				</a>
			);
		}

		if (/^(https?:)?\/\//.test(href)) {
			// External links: map to <a>
			return (
				<a
					{...(linkId && { "data-link-id": linkId })}
					tabIndex={tabindex}
					href={href}
					rel={target && "noreferrer"}
					target={target}
					ref={ref}
					{...rest}
				>
					{children}
				</a>
			);
		}

		const clickHandler = (event: React.MouseEvent<HTMLAnchorElement>) => {
			if (onClick) {
				onClick(event);
			}
			closeNav();
		};

		const cleanedUrl = fixStoryblokPath(href);
		// Internal links: map to <Link>
		if (target === "_blank") {
			return (
				<a
					{...(linkId && { "data-link-id": linkId })}
					href={`${cleanedUrl}${anchorFragment}`}
					rel="noopener noreferrer"
					target="_blank"
					ref={ref}
					tabIndex={tabindex}
					onClick={clickHandler}
					{...rest}
				>
					{children}
				</a>
			);
		}

		return (
			<GatsbyLink
				{...(linkId && { "data-link-id": linkId })}
				typeof="WebPage"
				tabIndex={tabindex}
				to={`${cleanedUrl}${anchorFragment}`}
				ref={
					ref as ((instance: GatsbyLink<unknown> | null) => void) &
						RefObject<HTMLAnchorElement>
				}
				onClick={clickHandler}
				{...rest}
			>
				{children}
			</GatsbyLink>
		);
	},
);

Link.displayName = "Link";
export default Link;
